/* Group imported image-and-caption product panels without rewriting article copy. */
(() => {
  const panelClass = 'newsletter-inline-product-panel';
  const gridClass = 'newsletter-inline-product-grid';
  const titleClass = 'newsletter-inline-product-title';
  const wrappers = new Set(['DIV', 'SPAN', 'P', 'FIGURE']);
  const captionTags = new Set(['BR', 'SPAN', 'STRONG', 'B', 'EM', 'I', 'SMALL']);

  const isSpacing = (node) => node.nodeType === Node.COMMENT_NODE ||
    (node.nodeType === Node.TEXT_NODE && !node.textContent.trim());
  const isBareWrapper = (node) => node.nodeType === Node.ELEMENT_NODE &&
    wrappers.has(node.tagName) && node.attributes.length === 0;
  const contentNodes = (node) => [...node.childNodes].filter((child) => !isSpacing(child));

  function productUrl(anchor) {
    try {
      const url = new URL(anchor.getAttribute('href'), document.baseURI);
      if (!/^https?:$/.test(url.protocol) || !/\/products\/[^/]+\/?$/.test(url.pathname)) return null;
      return `${url.origin}${url.pathname.replace(/\/$/, '')}`;
    } catch {
      return null;
    }
  }

  function captionAnchor(node, url) {
    while (isBareWrapper(node)) {
      const children = contentNodes(node);
      if (children.length !== 1) return null;
      node = children[0];
    }
    if (node.nodeType !== Node.ELEMENT_NODE || node.tagName !== 'A' ||
        productUrl(node) !== url || !node.textContent.trim()) return null;
    return [...node.querySelectorAll('*')].every((child) => captionTags.has(child.tagName)) ? node : null;
  }

  function makePanels(article) {
    for (const anchor of article.querySelectorAll('a[href]')) {
      if (anchor.closest(`.${panelClass}, .${gridClass}`)) continue;
      const url = productUrl(anchor);
      const imageNodes = contentNodes(anchor);
      // A single image link is intentionally stricter than arbitrary email markup.
      if (!url || imageNodes.length !== 1 || imageNodes[0].nodeType !== Node.ELEMENT_NODE ||
          imageNodes[0].tagName !== 'IMG') continue;

      let imageBranch = anchor;
      while (imageBranch.parentElement !== article && isBareWrapper(imageBranch.parentElement)) {
        const children = contentNodes(imageBranch.parentElement);
        if (children.length !== 1 || children[0] !== imageBranch) break;
        imageBranch = imageBranch.parentElement;
      }

      let captionBranch = imageBranch.nextSibling;
      while (captionBranch && isSpacing(captionBranch)) captionBranch = captionBranch.nextSibling;
      if (!captionBranch) continue;
      const caption = captionAnchor(captionBranch, url);
      if (!caption) continue;

      const panel = document.createElement('div');
      panel.className = panelClass;
      imageBranch.before(panel);
      // Move the original nodes, including whitespace and comments, without serializing them.
      let current = imageBranch;
      while (current) {
        const next = current.nextSibling;
        panel.append(current);
        if (current === captionBranch) break;
        current = next;
      }
      caption.classList.add(titleClass);
    }
  }

  function unwrapPanelContainers(article) {
    // Older email bodies nest each card and row in many attribute-free wrappers.
    for (const wrapper of [...article.querySelectorAll('div, span')].reverse()) {
      if (wrapper.attributes.length || wrapper.closest(`.${panelClass}, .${gridClass}`)) continue;
      const children = contentNodes(wrapper);
      if (children.length && children.every((child) => child.nodeType === Node.ELEMENT_NODE &&
          child.classList.contains(panelClass))) wrapper.replaceWith(...wrapper.childNodes);
    }
  }

  function groupPanels(article) {
    for (const panel of article.querySelectorAll(`.${panelClass}`)) {
      if (panel.closest(`.${gridClass}`)) continue;
      const grid = document.createElement('div');
      grid.className = gridClass;
      panel.before(grid);
      let current = panel;
      while (current) {
        if (!isSpacing(current) && !(current.nodeType === Node.ELEMENT_NODE &&
            current.classList.contains(panelClass))) break;
        const next = current.nextSibling;
        grid.append(current);
        current = next;
      }
    }
  }

  function enhance(scope) {
    const articles = [...scope.querySelectorAll('.article-template__content')];
    if (scope.matches?.('.article-template__content')) articles.unshift(scope);
    for (const article of articles) {
      // Explicit layouts preserve text links, chosen widths and reviewed rows.
      if (article.querySelector('.newsletter-link-layout')) continue;
      makePanels(article);
      unwrapPanelContainers(article);
      groupPanels(article);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => enhance(document), { once: true });
  } else {
    enhance(document);
  }
  document.addEventListener('shopify:section:load', (event) => enhance(event.target));
})();
